import React, {useEffect, useState} from 'react'
import _ from 'lodash'
import {Layer, Line, Rect, Text} from "react-konva";
import ImageShape from "./image-shape";
import TextPathShape from "./text-path-shape";
import TextShape from "./text-shape";
import GridLayer from "./grid-layer";
import DesignTransformer from "./design-transformer";
import {GUIDELINE_OFFSET} from "../../../constants/design.constant";
import Konva from "konva";
import RectangleShape from "./rectangle-shape";
import BackgroundImageItem from "./background-image-shape";
import BackgroundRectangleShape from "./background-rectangle-shape";

const RenderLayers = ({
                          designVariantDetail,
                          setDesignVariantDetail,
                          setSelectedImage,
                          selectedImage,
                          selectedText,
                          setSelectedText,
                          backgroundRef,
                          transformerRef,
                          selectionRectangleRef,
                          gridLayerRef,
                          stageRef,
                          showHighlightLines,
                          handleClick,
                          stagePos,
                          /*stageHeight,
                          stageWidth,*/
                      }) => {
    const layerRef = React.useRef(null);

    const handleImageAttributeChanges = (imageDetail, index, newAttrs) => {
        const existingDesignVariantDetail = {...designVariantDetail};
        existingDesignVariantDetail.images[index] = newAttrs;
        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleAttributeChanges = (textItem, index, newAttrs) => {
        const existingDesignVariantDetail = {...designVariantDetail};
        existingDesignVariantDetail.textItems[index] = newAttrs;

        delete existingDesignVariantDetail.textItems[index].width;
        delete existingDesignVariantDetail.textItems[index].height;
        delete existingDesignVariantDetail.textItems[index].offsetX;
        delete existingDesignVariantDetail.textItems[index].offsetY;

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleShapeAttributeChanges = (shapeDetail, index, newAttrs) => {
        const existingDesignVariantDetail = {...designVariantDetail};
        existingDesignVariantDetail.shapes[index] = newAttrs;

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const getLineGuideStops = (skipShape) => {
        const stage = stageRef.current;

        // we can snap to stage borders and the center of the stage
        let vertical = [0, stage.width() / 2, stage.width()];
        let horizontal = [0, stage.height() / 2, stage.height()];

        // and we snap over edges and center of each object on the canvas
        stage.find('.object').forEach((guideItem) => {
            if (guideItem === skipShape) {
                return;
            }
            let box = guideItem.getClientRect();
            // and we can snap to all edges of shapes
            vertical.push([box.x, box.x + box.width, box.x + box.width / 2]);
            horizontal.push([box.y, box.y + box.height, box.y + box.height / 2]);
        });

        return {
            vertical: vertical.flat(),
            horizontal: horizontal.flat(),
        };
    }

    const getObjectSnappingEdges = (node) => {
        let box = node.getClientRect();
        let absPos = node.absolutePosition();

        return {
            vertical: [
                {
                    guide: Math.round(box.x),
                    offset: Math.round(absPos.x - box.x),
                    snap: 'start',
                },
                {
                    guide: Math.round(box.x + box.width / 2),
                    offset: Math.round(absPos.x - box.x - box.width / 2),
                    snap: 'center',
                },
                {
                    guide: Math.round(box.x + box.width),
                    offset: Math.round(absPos.x - box.x - box.width),
                    snap: 'end',
                },
            ],
            horizontal: [
                {
                    guide: Math.round(box.y),
                    offset: Math.round(absPos.y - box.y),
                    snap: 'start',
                },
                {
                    guide: Math.round(box.y + box.height / 2),
                    offset: Math.round(absPos.y - box.y - box.height / 2),
                    snap: 'center',
                },
                {
                    guide: Math.round(box.y + box.height),
                    offset: Math.round(absPos.y - box.y - box.height),
                    snap: 'end',
                },
            ],
        };
    }

    const getGuides = (lineGuideStops, itemBounds) => {
        let resultV = [];
        let resultH = [];

        lineGuideStops.vertical.forEach((lineGuide) => {
            itemBounds.vertical.forEach((itemBound) => {
                let diff = Math.abs(lineGuide - itemBound.guide);
                // if the distance between guild line and object snap point is close we can consider this for snapping
                if (diff < GUIDELINE_OFFSET) {
                    resultV.push({
                        lineGuide: lineGuide,
                        diff: diff,
                        snap: itemBound.snap,
                        offset: itemBound.offset,
                    });
                }
            });
        });

        lineGuideStops.horizontal.forEach((lineGuide) => {
            itemBounds.horizontal.forEach((itemBound) => {
                let diff = Math.abs(lineGuide - itemBound.guide);
                if (diff < GUIDELINE_OFFSET) {
                    resultH.push({
                        lineGuide: lineGuide,
                        diff: diff,
                        snap: itemBound.snap,
                        offset: itemBound.offset,
                    });
                }
            });
        });

        let guides = [];

        // find closest snap
        let minV = resultV.sort((a, b) => a.diff - b.diff)[0];
        let minH = resultH.sort((a, b) => a.diff - b.diff)[0];
        if (minV) {
            guides.push({
                lineGuide: minV.lineGuide,
                offset: minV.offset,
                orientation: 'V',
                snap: minV.snap,
            });
        }
        if (minH) {
            guides.push({
                lineGuide: minH.lineGuide,
                offset: minH.offset,
                orientation: 'H',
                snap: minH.snap,
            });
        }
        return guides;
    }

    const drawGuides = (guides) => {
        guides.forEach((lg) => {
            if (lg.orientation === 'H') {
                let line = new Konva.Line({
                    points: [-6000, 0, 6000, 0],
                    stroke: 'rgb(0, 161, 255)',
                    strokeWidth: 10,
                    name: 'guid-line',
                    dash: [40, 60],
                });

                layerRef.current.add(line);
                line.absolutePosition({
                    x: 0,
                    y: lg.lineGuide,
                });
            } else if (lg.orientation === 'V') {
                let line = new Konva.Line({
                    points: [0, -6000, 0, 6000],
                    stroke: 'rgb(0, 161, 255)',
                    strokeWidth: 10,
                    name: 'guid-line',
                    dash: [40, 60],
                });

                layerRef.current.add(line);
                line.absolutePosition({
                    x: lg.lineGuide,
                    y: 0,
                });
            }
        });
    }

    const handleGuidelineDragMove = (e) => {
        // console.log('handleGuidelineDragMove: ', e)
        layerRef.current.find('.guid-line').forEach((l) => l.destroy());

        // find possible snapping lines
        let lineGuideStops = getLineGuideStops(e.target);
        // find snapping points of current object
        let itemBounds = getObjectSnappingEdges(e.target);

        // now find where can we snap current object
        let guides = getGuides(lineGuideStops, itemBounds);

        // do nothing of no snapping
        if (!guides.length) {
            return;
        }

        drawGuides(guides);

        let absPos = e.target.absolutePosition();
        // now force object position
        guides.forEach((lg) => {
            switch (lg.snap) {
                case 'start': {
                    switch (lg.orientation) {
                        case 'V': {
                            absPos.x = lg.lineGuide + lg.offset;
                            break;
                        }
                        case 'H': {
                            absPos.y = lg.lineGuide + lg.offset;
                            break;
                        }
                    }
                    break;
                }
                case 'center': {
                    switch (lg.orientation) {
                        case 'V': {
                            absPos.x = lg.lineGuide + lg.offset;
                            break;
                        }
                        case 'H': {
                            absPos.y = lg.lineGuide + lg.offset;
                            break;
                        }
                    }
                    break;
                }
                case 'end': {
                    switch (lg.orientation) {
                        case 'V': {
                            absPos.x = lg.lineGuide + lg.offset;
                            break;
                        }
                        case 'H': {
                            absPos.y = lg.lineGuide + lg.offset;
                            break;
                        }
                    }
                    break;
                }
            }
        });

        e.target.absolutePosition(absPos);
    }

    const handleGuidelineDragEnd = (e) => {
        layerRef.current.find('.guid-line').forEach((l) => l.destroy());
    }

    return (
        <>
            <Layer>
                <BackgroundRectangleShape
                    backgroundRef={backgroundRef}
                    background={designVariantDetail?.settings?.background}
                    stageWidth={designVariantDetail?.settings?.width}
                    stageHeight={designVariantDetail?.settings?.height}/>
            </Layer>

            <Layer>
                {designVariantDetail?.images?.map((imageDetail, index) => {
                    return <BackgroundImageItem
                        key={`repeat-image-${index}-${imageDetail.key}`}
                        stageWidth={designVariantDetail?.settings?.width}
                        stageHeight={designVariantDetail?.settings?.height}
                        backgroundImage={imageDetail}
                        settings={designVariantDetail?.settings}/>

                })}
            </Layer>

            <Layer ref={gridLayerRef}>
                {showHighlightLines &&
                    <GridLayer
                        stageWidth={designVariantDetail?.settings?.width}
                        stageHeight={designVariantDetail?.settings?.height}/>
                }
            </Layer>

            <Layer ref={layerRef}>

                {designVariantDetail?.images?.map((imageDetail, index) => {
                    return <ImageShape
                        key={`image-${index}-${imageDetail.key}`}
                        shapeProps={imageDetail}
                        isSelected={imageDetail.key === selectedImage?.key}
                        onSelect={() => {
                            setSelectedImage(imageDetail);
                            setSelectedText(null);
                        }}
                        onChange={(newAttrs) => {
                            handleImageAttributeChanges(imageDetail, index, newAttrs);
                        }}
                        handleGuidelineDragEnd={handleGuidelineDragEnd}
                        handleGuidelineDragMove={handleGuidelineDragMove}
                    />
                })}

                {designVariantDetail?.shapes?.map((shapeDetail, index) => {
                    return <RectangleShape
                        key={shapeDetail?.key}
                        shapeProps={shapeDetail}
                        onChange={(newAttrs) => {
                            handleShapeAttributeChanges(shapeDetail, index, newAttrs);
                        }}
                        handleGuidelineDragEnd={handleGuidelineDragEnd}
                        handleGuidelineDragMove={handleGuidelineDragMove}/>
                })}

                {!designVariantDetail?.settings?.noText &&
                    <>
                        {designVariantDetail?.textItems?.map((textItem, index) => {
                            return textItem?.radius ? <TextPathShape
                                key={textItem?.key}
                                shapeProps={textItem}
                                isSelected={textItem?.key === selectedText}
                                onSelect={undefined}
                                onChange={(newAttrs) => {
                                    handleAttributeChanges(textItem, index, newAttrs);
                                }}
                                transformerRef={transformerRef}
                                handleGuidelineDragEnd={handleGuidelineDragEnd}
                                handleGuidelineDragMove={handleGuidelineDragMove}
                            /> : <TextShape
                                key={textItem?.key}
                                shapeProps={textItem}
                                isSelected={textItem?.key === selectedText}
                                onSelect={undefined}
                                onChange={(newAttrs) => {
                                    handleAttributeChanges(textItem, index, newAttrs);
                                }}
                                stagePos={stagePos}
                                transformerRef={transformerRef}
                                handleGuidelineDragEnd={handleGuidelineDragEnd}
                                handleGuidelineDragMove={handleGuidelineDragMove}
                            />

                        })}
                    </>
                }

                <DesignTransformer
                    transformerRef={transformerRef}
                    selectionRectangleRef={selectionRectangleRef}
                />

            </Layer>

        </>
    )
}

export default RenderLayers
